import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { Upload, Eye, Edit, Trash2, User, X } from 'lucide-react';
import { useTeachers } from './hooks/useTeachers';
import { useSchools } from './hooks/useSchools';
import { useDebounce } from '../../hooks/useDebounce';
import { TEACHER_STATUSES } from './constants';
import type { SchoolTeacher, SchoolTeacherFilters, SchoolTeacherCreateRequest } from './types';
import CreateSchoolTeacherModal from './components/CreateSchoolTeacherModal';
import ImportSchoolTeacherModal from './components/ImportSchoolTeacherModal';
import SchoolTeacherDetail from './components/SchoolTeacherDetail';
import { schoolService } from '../../services';
import { buildTeacherUpdatePayload } from './utils/buildTeacherUpdatePayload';
import { notifyAccountCreated } from '../../components/notifyAccountCreated';
import AdminListSortSelect from '../../components/ui/AdminListSortSelect';

const normalizeSchoolTeacher = (teacher: any): SchoolTeacher => {
  const metadata = teacher.metadata || {};

  return {
    id: teacher.id || teacher._id,
    name: teacher.user?.name || teacher.name || '',
    email: teacher.user?.email || teacher.email || '',
    phone: teacher.user?.phone || teacher.phone || '',
    code: teacher.user?.code || teacher.code || '',
    schoolId: teacher.schoolId || teacher.school?.id || '',
    schoolName: teacher.school?.name || teacher.schoolName || '',
    status: teacher.status,
    unit: metadata.unit,
    subjects: metadata.subjects || [],
    classification: metadata.classification,
    monthlyQuota: metadata.monthlyQuota,
    position: metadata.position,
    location: metadata.location,
    instructorType: metadata.instructorType,
    skills: metadata.skills || [],
    certifications: metadata.certifications || [],
    hourlyRate: metadata.hourlyRate,
    rating: metadata.rating,
    totalClasses: metadata.totalClasses,
    classId: teacher.classId || teacher.class?.id || metadata.classId,
    className: teacher.class?.name,
    schoolYear: teacher.class?.schoolYear,
    grade: teacher.class?.grade,
    metadata,
    createdAt: teacher.createdAt || teacher.created_at || '',
    updatedAt: teacher.updatedAt || teacher.updated_at || '',
  };
};

// SchoolTeacherDashboard component with school column
const SchoolTeacherDashboard: React.FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [viewingTeacher, setViewingTeacher] = useState<SchoolTeacher | null>(null);
  const [editingTeacher, setEditingTeacher] = useState<SchoolTeacher | null>(null);
  
  // Local state for search input (before debouncing)
  const [searchInput, setSearchInput] = useState('');
  
  // Debounce search input (wait 500ms after user stops typing)
  const debouncedSearch = useDebounce(searchInput, 500);
  
  const {
    teachers,
    pagination,
    filters,
    isLoading,
    error,
    updateFilters,
    updatePagination,
    resetFilters,
    refetch,
    isDeleting,
    isTogglingStatus,
    isCreating,
    isUpdating,
    createTeacherMutation,
    updateTeacherMutation,
    deleteTeacherMutation,
    toggleStatusMutation
  } = useTeachers();

  // Fetch schools for the filter dropdown
  const { data: schoolsResponse, isLoading: isLoadingSchools } = useSchools({
    per_page: 1000, // Get all schools for the dropdown
    enabled: true
  });
  
  const allSchools = schoolsResponse?.data || [];
  
  // Update filters when debounced search value changes
  useEffect(() => {
    updateFilters({ search: debouncedSearch });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const tableScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    tableScrollRef.current?.scrollTo({ top: 0 });
  }, [pagination?.page, pagination?.per_page, filters]);

  const handleFilterChange = (key: keyof SchoolTeacherFilters, value: string) => {
    updateFilters({ [key]: value });
  };

  const handlePageChange = (page: number) => {
    updatePagination(page);
  };

  const handlePerPageChange = (perPage: number) => {
    updatePagination(1, perPage);
  };

  const handleImportTeachers = () => {
    setIsImportModalOpen(true);
  };

  // Lỗi được ném lại để modal hiện theo từng ô và giữ nguyên dữ liệu đã nhập
  const handleCreateTeacherSubmit = async (data: SchoolTeacherCreateRequest) => {
    const created = await createTeacherMutation.mutateAsync(data);
    setIsCreateModalOpen(false);
    notifyAccountCreated(`Tạo giáo viên "${data.name}" thành công!`, created);
    refetch(); // Refresh the list after creating
  };

  const loadTeacher = async (teacherId: string): Promise<SchoolTeacher> => {
    const teacher = await schoolService.getSchoolTeacher(teacherId);
    return normalizeSchoolTeacher(teacher);
  };

  const handleViewTeacher = async (teacherId: string) => {
    try {
      setViewingTeacher(await loadTeacher(teacherId));
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Có lỗi xảy ra khi tải thông tin giáo viên');
    }
  };

  const handleEditTeacher = async (teacherId: string) => {
    try {
      setEditingTeacher(await loadTeacher(teacherId));
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Có lỗi xảy ra khi tải thông tin giáo viên');
    }
  };

  const handleUpdateTeacherSubmit = async (data: SchoolTeacherCreateRequest) => {
    if (!editingTeacher) return;

    const updateData = buildTeacherUpdatePayload(editingTeacher, data);
    if (!updateData.email) delete updateData.email;
    if (!updateData.phone) delete updateData.phone;

    try {
      await updateTeacherMutation.mutateAsync({ id: editingTeacher.id, data: updateData });
      setEditingTeacher(null);
      toast.success(`Cập nhật giáo viên "${data.name}" thành công!`);
      refetch();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Có lỗi xảy ra khi cập nhật giáo viên');
    }
  };

  const handleDeleteTeacher = (teacherId: string, teacherName: string) => {
    toast((t) => (
      <div className="flex flex-col space-y-3">
        <div className="flex items-center space-x-2">
          <span className="text-lg">⚠️</span>
          <span className="font-medium">Xác nhận xóa giáo viên</span>
        </div>
        <p className="text-sm text-gray-600">
          Bạn có chắc chắn muốn xóa giáo viên <strong>"{teacherName}"</strong>?
        </p>
        <div className="flex space-x-2 justify-end">
          <button
            onClick={() => {
              toast.dismiss(t.id);
            }}
            className="px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded transition-colors"
          >
            Hủy
          </button>
          <button
            onClick={() => {
              toast.dismiss(t.id);
              toast.promise(
                deleteTeacherMutation.mutateAsync(teacherId),
                {
                  loading: 'Đang xóa giáo viên...',
                  success: `Xóa giáo viên "${teacherName}" thành công!`,
                  error: (err) => err?.response?.data?.message || 'Có lỗi xảy ra khi xóa giáo viên',
                }
              );
            }}
            className="px-3 py-1 text-sm bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
          >
            Xóa
          </button>
        </div>
      </div>
    ), {
      duration: Infinity,
      style: {
        background: '#fff',
        color: '#000',
        maxWidth: '400px',
      },
    });
  };

  const handleToggleStatus = (teacherId: string, teacherName: string, currentStatus: string) => {
    const actionText = currentStatus === TEACHER_STATUSES.ACTIVE ? 'tạm dừng' : 'kích hoạt';
    toast((t) => (
      <div className="flex flex-col space-y-3">
        <div className="flex items-center space-x-2">
          <span className="text-lg">{currentStatus === TEACHER_STATUSES.ACTIVE ? '⏸️' : '▶️'}</span>
          <span className="font-medium">Xác nhận {actionText} giáo viên</span>
        </div>
        <p className="text-sm text-gray-600">
          Bạn có chắc chắn muốn {actionText} giáo viên <strong>"{teacherName}"</strong>?
        </p>
        <div className="flex space-x-2 justify-end">
          <button
            onClick={() => {
              toast.dismiss(t.id);
            }}
            className="px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded transition-colors"
          >
            Hủy
          </button>
          <button
            onClick={() => {
              toast.dismiss(t.id);
              toast.promise(
                toggleStatusMutation.mutateAsync({
                  id: teacherId,
                  status: currentStatus === TEACHER_STATUSES.ACTIVE
                    ? TEACHER_STATUSES.INACTIVE
                    : TEACHER_STATUSES.ACTIVE,
                }),
                {
                  loading: `Đang ${actionText} giáo viên...`,
                  success: `${actionText.charAt(0).toUpperCase() + actionText.slice(1)} giáo viên "${teacherName}" thành công!`,
                  error: (err) => err?.response?.data?.message || `Có lỗi xảy ra khi ${actionText} giáo viên`,
                }
              );
            }}
            className={`px-3 py-1 text-sm text-white rounded transition-colors ${
              currentStatus === TEACHER_STATUSES.ACTIVE 
                ? 'bg-orange-600 hover:bg-orange-700' 
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {actionText.charAt(0).toUpperCase() + actionText.slice(1)}
          </button>
        </div>
      </div>
    ), {
      duration: Infinity,
      style: {
        background: '#fff',
        color: '#000',
        maxWidth: '400px',
      },
    });
  };

  const handleImportSuccess = () => {
    refetch(); // Refresh the list after importing
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, string> = {
      [TEACHER_STATUSES.ACTIVE]: 'bg-green-100 text-green-800',
      [TEACHER_STATUSES.INACTIVE]: 'bg-gray-100 text-gray-800',
      [TEACHER_STATUSES.ON_LEAVE]: 'bg-yellow-100 text-yellow-800'
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusConfig[status] || 'bg-gray-100 text-gray-800'}`}>
        {status === TEACHER_STATUSES.ACTIVE && 'Hoạt động'}
        {status === TEACHER_STATUSES.INACTIVE && 'Không hoạt động'}
        {status === TEACHER_STATUSES.ON_LEAVE && 'Nghỉ phép'}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quản lý Giáo viên</h1>
          <p className="text-gray-600 mt-1">
            Quản lý danh sách giáo viên, thông tin và hiệu suất giảng dạy
          </p>
        </div>
        <div className="flex space-x-3">
          {/* <button 
            onClick={handleCreateTeacher}
            disabled={isCreating}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {isCreating ? 'Đang tạo...' : '+ Thêm giáo viên mới'}
          </button> */}
            <button
            onClick={handleImportTeachers}
            disabled={isCreating}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import từ Excel
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              </div>
              <input
                type="text"
                placeholder="Tìm kiếm theo tên, số điện thoại..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
              {(searchInput !== debouncedSearch || (debouncedSearch && isLoading)) && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <select 
              value={filters.schoolId || 'all'} 
              onChange={(e) => {
                const value = e.target.value === 'all' ? '' : e.target.value;
                handleFilterChange('schoolId', value);
              }}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isLoadingSchools}
            >
              <option value="all">
                {isLoadingSchools ? 'Đang tải trường học...' : 'Tất cả trường học'}
              </option>
              {allSchools.map((school) => (
                <option key={school.id} value={school.id}>{school.name}</option>
              ))}
            </select>
            <select 
              value={filters.status || 'all'} 
              onChange={(e) => handleFilterChange('status', e.target.value === 'all' ? '' : e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tất cả trạng thái</option>
              <option value={TEACHER_STATUSES.ACTIVE}>Hoạt động</option>
              <option value={TEACHER_STATUSES.INACTIVE}>Không hoạt động</option>
              <option value={TEACHER_STATUSES.ON_LEAVE}>Nghỉ phép</option>
            </select>
            <AdminListSortSelect
              value={filters.sort}
              onChange={(value) => handleFilterChange('sort', value)}
              disabled={isLoading}
            />
            <button
              onClick={() => {
                resetFilters();
                toast.success('Đã reset bộ lọc', {
                  icon: '🔄',
                  duration: 2000,
                });
              }}
              className="px-3 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="space-y-6">
          {/* Header Skeleton */}
          <div className="flex justify-between items-start">
            <div>
              <div className="h-8 w-64 bg-gray-200 rounded animate-pulse mb-1"></div>
              <div className="h-4 w-80 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div className="h-10 w-40 bg-gray-200 rounded-lg animate-pulse"></div>
          </div>

          {/* Search and Filters Skeleton */}
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <div className="h-10 bg-gray-200 rounded-md animate-pulse"></div>
              </div>
              <div className="flex gap-3">
                <div className="h-10 w-40 bg-gray-200 rounded-md animate-pulse"></div>
                <div className="h-10 w-32 bg-gray-200 rounded-md animate-pulse"></div>
                <div className="h-10 w-24 bg-gray-200 rounded-md animate-pulse"></div>
              </div>
            </div>
          </div>

          {/* Teachers List Skeleton */}
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="h-6 w-48 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                      <th key={i} className="px-6 py-3 text-left">
                        <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-12 w-12 bg-gray-200 rounded-lg"></div>
                          <div className="ml-4">
                            <div className="h-4 w-32 bg-gray-200 rounded"></div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-4 w-20 bg-gray-200 rounded"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-4 w-24 bg-gray-200 rounded"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-4 w-20 bg-gray-200 rounded"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-6 w-24 bg-gray-200 rounded-full"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="h-6 w-24 bg-gray-200 rounded-full"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex space-x-2">
                          <div className="h-8 w-8 bg-gray-200 rounded-lg"></div>
                          <div className="h-8 w-8 bg-gray-200 rounded-lg"></div>
                          <div className="h-8 w-8 bg-gray-200 rounded-lg"></div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
        </div>
        
            {/* Pagination Skeleton */}
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="h-4 w-48 bg-gray-200 rounded animate-pulse"></div>
                  <div className="flex items-center space-x-2">
                    <div className="h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-8 w-16 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 w-8 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="h-8 w-16 bg-gray-200 rounded animate-pulse"></div>
                  <div className="flex space-x-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
                    ))}
                  </div>
                  <div className="h-8 w-16 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Teachers List */}
      {!isLoading && (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Danh sách Giáo viên</h2>
            {isLoading && (
              <div className="flex items-center text-sm text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                Đang cập nhật...
              </div>
        )}
      </div>
          {/* 420px = như trang Học sinh (360px) + hàng tiêu đề "Danh sách Giáo viên" */}
          <div ref={tableScrollRef} className="overflow-auto max-h-[max(420px,calc(100vh-420px))]">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10 [&_th]:shadow-[0_1px_0_0_#e5e7eb]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    GIÁO VIÊN
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    TRƯỜNG HỌC
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    LỚP HỌC
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    VAI TRÒ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    TRẠNG THÁI
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    HÀNH ĐỘNG
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {error ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center space-y-4">
                        <div className="text-red-500">
                          <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="text-center">
                          <h3 className="text-lg font-medium text-red-600 mb-2">Lỗi tải dữ liệu</h3>
                          <p className="text-gray-600 mb-4">{error?.message || 'Đã xảy ra lỗi khi tải danh sách giáo viên'}</p>
                          <button 
                            onClick={() => {
                              toast.promise(
                                refetch(),
                                {
                                  loading: 'Đang tải lại dữ liệu...',
                                  success: 'Tải dữ liệu thành công!',
                                  error: 'Không thể tải dữ liệu',
                                }
                              );
                            }}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            Thử lại
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : teachers.length === 0 && !isLoading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      Không tìm thấy giáo viên nào
                    </td>
                  </tr>
                ) : (
                  teachers.map((teacher: any) => (
                    <tr key={teacher.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-12 w-12">
                            <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                              <User className="h-6 w-6 text-blue-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <button
                              type="button"
                              onClick={() => handleViewTeacher(teacher.id)}
                              className="text-sm font-medium text-blue-600 hover:text-blue-900 hover:underline"
                            >
                              {teacher.user?.name || 'N/A'}
                            </button>
                            <div className="text-sm text-gray-500">{teacher.user?.email || 'N/A'}</div>
                            <div className="text-xs text-gray-400">{teacher.user?.code || 'N/A'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{teacher.school?.name || '-'}</div>
                        <div className="text-xs text-gray-500">{teacher.school?.code || ''}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{teacher.class?.name || '-'}</div>
                        <div className="text-xs text-gray-500">
                          {teacher.class ? `${teacher.class.grade} - ${teacher.class.schoolYear}` : ''}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          {teacher.role === 'teacher' ? 'Giáo viên' : teacher.role || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(teacher.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            type="button"
                            onClick={() => handleViewTeacher(teacher.id)}
                            className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Xem chi tiết"
                          >
                            <Eye className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleEditTeacher(teacher.id)}
                            className="p-2 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-lg transition-colors"
                            title="Chỉnh sửa"
                          >
                            <Edit className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(teacher.id, teacher.user?.name || 'N/A', teacher.status)}
                            disabled={isTogglingStatus}
                            className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                              teacher.status === TEACHER_STATUSES.ACTIVE 
                                ? 'text-orange-600 hover:text-orange-900 hover:bg-orange-50' 
                                : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                            }`}
                            title={teacher.status === TEACHER_STATUSES.ACTIVE ? 'Tạm dừng hoạt động' : 'Kích hoạt hoạt động'}
                          >
                            {teacher.status === TEACHER_STATUSES.ACTIVE ? (
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            ) : (
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            )}
                          </button>
                          <button
                            onClick={() => handleDeleteTeacher(teacher.id, teacher.user?.name || 'N/A')}
                            disabled={isDeleting}
                            className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Xóa giáo viên"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          
          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="text-sm text-gray-700">
                    Hiển thị {((pagination.page - 1) * pagination.per_page) + 1} đến {Math.min(pagination.page * pagination.per_page, pagination.total)} 
                    trong tổng số {pagination.total} giáo viên
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700">Hiển thị:</span>
                    <select
                      value={pagination.per_page}
                      onChange={(e) => handlePerPageChange(Number(e.target.value))}
                      className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-sm text-gray-700">mục</span>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Trước
                  </button>
                  
                  {/* Page Numbers */}
                  <div className="flex items-center space-x-1">
                    {(() => {
                      const totalPages = pagination.totalPages;
                      const currentPage = pagination.page;
                      const pages = [];
                      
                      // Always show first page
                      if (currentPage > 3) {
                        pages.push(
                          <button
                            key={1}
                            onClick={() => handlePageChange(1)}
                            className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                          >
                            1
                          </button>
                        );
                        if (currentPage > 4) {
                          pages.push(
                            <span key="ellipsis1" className="px-2 text-gray-500">
                              ...
                            </span>
                          );
                        }
                      }
                      
                      // Show pages around current page
                      for (let i = Math.max(1, currentPage - 2); i <= Math.min(totalPages, currentPage + 2); i++) {
                        pages.push(
                          <button
                            key={i}
                            onClick={() => handlePageChange(i)}
                            className={`px-3 py-1 text-sm border rounded-md ${
                              i === currentPage
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {i}
                          </button>
                        );
                      }
                      
                      // Always show last page
                      if (currentPage < totalPages - 2) {
                        if (currentPage < totalPages - 3) {
                          pages.push(
                            <span key="ellipsis2" className="px-2 text-gray-500">
                              ...
                            </span>
                          );
                        }
                        pages.push(
          <button
                            key={totalPages}
                            onClick={() => handlePageChange(totalPages)}
                            className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
                            {totalPages}
          </button>
                        );
                      }
                      
                      return pages;
                    })()}
                  </div>
                  
          <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                    className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
                    Sau
          </button>
        </div>
      </div>
            </div>
          )}
        </div>
      )}

      <CreateSchoolTeacherModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleCreateTeacherSubmit}
        isLoading={isCreating}
      />

      <CreateSchoolTeacherModal
        isOpen={!!editingTeacher}
        onClose={() => setEditingTeacher(null)}
        editingTeacher={editingTeacher}
        onSave={handleUpdateTeacherSubmit}
        isLoading={isUpdating}
      />

      {viewingTeacher && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          onClick={() => setViewingTeacher(null)}
        >
          <div
            className="relative max-h-[90vh] w-full max-w-5xl overflow-y-auto"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setViewingTeacher(null)}
              className="absolute right-4 top-4 z-10 rounded-lg bg-white p-2 text-gray-500 shadow hover:text-gray-700"
              title="Đóng"
            >
              <X className="h-5 w-5" />
            </button>
            <SchoolTeacherDetail teacher={viewingTeacher} />
          </div>
        </div>
      )}

        <ImportSchoolTeacherModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
          onImportSuccess={handleImportSuccess}
        />
    </div>
  );
};

export default SchoolTeacherDashboard;
